import { LocationPros } from "../types/baseTypes";

/**
 * Reading a location's address without assuming it has one.
 *
 * `LocationPros.address` is typed non-optional, but `location.address_id` is
 * **nullable** in both schemas and the API serializes a missing address as
 * `null` (`locationService.getAllByType` → `addressMap[address_id] || null`).
 * Every location created by the station wizard before #2433 has none. Types are
 * not evidence of a runtime shape (CLAUDE.md, ADR-0007 amendment 2026-07-18) —
 * and the failure is not a blank field but a `TypeError` that takes the whole
 * picker down, which reads to the customer as a broken app.
 *
 * One place rather than eight guards: a location's address is one question.
 *
 * THE COPY THAT CANNOT IMPORT THIS: `frontend/admin-ui/src/utils/addressLine.ts`
 * — a separate deployable answering the same question for the operator. The
 * house-number rule below is that file's, kept identical on purpose: change one
 * and change the other, or the same station reads differently in the admin and
 * in the app. `addressLine.test.ts` next to it pins the same cases as
 * `locationAddress.test.ts` does here.
 */

type MaybeAddress = Partial<LocationPros["address"]> | null | undefined;

const clean = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";

export const streetOf = (location?: { address?: MaybeAddress } | null): string =>
    clean(location?.address?.street);

export const cityOf = (location?: { address?: MaybeAddress } | null): string =>
    clean(location?.address?.city);

export const districtOf = (location?: { address?: MaybeAddress } | null): string =>
    clean(location?.address?.district);

export const postalCodeOf = (location?: { address?: MaybeAddress } | null): string =>
    clean(location?.address?.postalCode);

export const houseNumberOf = (location?: { address?: MaybeAddress } | null): string =>
    clean(location?.address?.houseNumber);

/**
 * "Paulinerstr. 8" — the street with its number.
 *
 * The number is appended only when the street does not already end in it:
 * tenant data carries it BOTH ways, because operators type "Paulinerstr. 8"
 * into the street field AND "8" beside it, which would render as
 * "Paulinerstr. 8 8" (staging, PO 2026-08-31).
 */
export const streetLineOf = (location?: { address?: MaybeAddress } | null): string => {
    const streetName = streetOf(location);
    const houseNumber = houseNumberOf(location);
    const alreadyInStreet = houseNumber.length > 0
        && new RegExp(`(^|\\s)${houseNumber.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}$`).test(streetName);
    return [streetName, alreadyInStreet ? "" : houseNumber]
        .filter(Boolean)
        .join(" ");
};

/** "04356 Leipzig", or "" when neither part is known. */
export const townOf = (location?: { address?: MaybeAddress } | null): string =>
    [postalCodeOf(location), cityOf(location)].filter(Boolean).join(" ");

/**
 * "Messe-Allee, 04356 Leipzig", or "" when the location carries no address.
 * Never a half-built string with a dangling comma.
 */
export const addressLineOf = (
    location?: { address?: MaybeAddress } | null,
): string => [streetLineOf(location), townOf(location)].filter(Boolean).join(", ");

/** The same line built straight from an address object rather than a location. */
export const addressLine = (address: MaybeAddress): string =>
    addressLineOf({ address });
